import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, UsersThree, ListChecks, Trash, PaperPlaneTilt } from "@phosphor-icons/react";

export default function ClubSquads() {
  const [squads, setSquads] = useState(null);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [err, setErr] = useState("");
  const [managing, setManaging] = useState(null); // squad object

  const [confirmDel, setConfirmDel] = useState(null); // squad object pending delete
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [s, p, u] = await Promise.all([
      api.get("/squads"), api.get("/players"), api.get("/users"),
    ]);
    setSquads(s.data); setPlayers(p.data); setUsers(u.data);
  };
  useEffect(() => { refresh(); }, []);

  const coaches = users.filter((u) => u.role === "coach" || u.role === "head_coach" || u.role === "external_coach");

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/squads", form);
      setForm({ name: "", description: "" });
      setOpen(false);
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/squads/${confirmDel.id}`);
      setConfirmDel(null);
      await refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="club-squads">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label">Club</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Squads</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Group players into squads, then dispatch a squad to a coach in one click — every player gets assigned automatically.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-squad-btn">
          <Plus size={16} weight="bold" /> New squad
        </button>
      </div>

      {!squads && <div className="skeleton h-32" />}
      {squads?.length === 0 && <div className="surface p-6 text-zinc-400 text-sm">No squads yet.</div>}

      <div className="grid gap-3">
        {squads?.map((s) => (
          <div key={s.id} className="surface p-4" data-testid={`squad-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex items-start gap-3">
                <div className="w-11 h-11 rounded-md bg-[#1a1a1a] border border-[#27272a] flex items-center justify-center shrink-0">
                  <ListChecks size={20} className="text-[#dfff00]" weight="duotone" />
                </div>
                <div>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {s.players?.length || 0} players •{" "}
                    {s.coach ? <span className="text-[#dfff00]">Dispatched to {s.coach.name}</span> : <span className="text-zinc-500">Not yet dispatched</span>}
                  </div>
                  {s.description && <div className="text-sm text-zinc-400 mt-1.5">{s.description}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setManaging(s)} className="btn-secondary text-sm" data-testid={`manage-${s.id}`}>
                  Manage
                </button>
                <button onClick={() => setConfirmDel(s)} className="btn-ghost text-[#ff0026]" data-testid={`delete-squad-${s.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>
            {s.players?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.players.map((p) => (
                  <span key={p.id} className="text-xs px-2 py-1 bg-[#1a1a1a] border border-[#27272a] rounded-md text-zinc-300">
                    {p.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-squad-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase text-xl">New squad</h2>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="font-label block mb-1">Name</label>
                <input required className="input" placeholder="e.g. U12 Performance" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="squad-name" />
              </div>
              <div>
                <label className="font-label block mb-1">Description (optional)</label>
                <textarea rows={2} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              {err && <div className="text-sm text-[#ff0026]">{err}</div>}
              <button type="submit" className="btn-primary w-full" data-testid="squad-submit">Create squad</button>
            </div>
          </form>
        </div>
      )}

      {managing && (
        <ManageSquadModal squad={managing} players={players} coaches={coaches} onClose={() => setManaging(null)} onChange={refresh} />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" onClick={() => !busy && setConfirmDel(null)} data-testid="confirm-delete-modal">
          <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#ff0026]/10 border border-[#ff0026]/40 flex items-center justify-center shrink-0">
                <Trash size={20} className="text-[#ff0026]" weight="bold" />
              </div>
              <div>
                <h2 className="font-heading uppercase text-xl tracking-tight">Delete squad?</h2>
                <p className="text-sm text-zinc-400 mt-1">
                  <span className="text-white font-semibold">{confirmDel.name}</span> will be removed.
                  {confirmDel.coach ? (
                    <> Active coach assignments to <span className="text-white">{confirmDel.coach.name}</span> created by this squad will end.</>
                  ) : null}
                  {" "}Player records and term evaluations are kept.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} disabled={busy} className="btn-secondary" data-testid="confirm-delete-cancel">Cancel</button>
              <button onClick={confirmDelete} disabled={busy} className="btn-primary bg-[#ff0026] text-white hover:bg-[#ff1a3a]" style={{ background: "#ff0026", color: "white" }} data-testid="confirm-delete-confirm">
                {busy ? "Deleting…" : "Delete squad"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManageSquadModal({ squad, players, coaches, onClose, onChange }) {
  const [picker, setPicker] = useState("");
  const [coachPick, setCoachPick] = useState(squad.coach_user_id || "");
  const [busy, setBusy] = useState(false);
  const inSquad = new Set((squad.players || []).map((p) => p.id));
  const remaining = players.filter((p) => !inSquad.has(p.id));

  const add = async () => {
    if (!picker) return;
    setBusy(true);
    await api.post(`/squads/${squad.id}/players`, { player_id: picker });
    setBusy(false);
    setPicker("");
    onChange();
  };

  const remove = async (pid) => {
    setBusy(true);
    await api.delete(`/squads/${squad.id}/players/${pid}`);
    setBusy(false);
    onChange();
  };

  const dispatchSquad = async () => {
    if (!coachPick) return;
    setBusy(true);
    const res = await api.post(`/squads/${squad.id}/dispatch`, { coach_user_id: coachPick });
    setBusy(false);
    alert(`Dispatched. ${res.data.new_assignments} new player assignment(s) created.`);
    onChange();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="manage-squad-modal">
      <div className="surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label">Squad</div>
            <h2 className="font-heading uppercase text-xl">{squad.name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        <div className="mb-5">
          <div className="font-label mb-2">Players in this squad</div>
          {(squad.players || []).length === 0 && <div className="text-sm text-zinc-500 mb-2">No players yet.</div>}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(squad.players || []).map((p) => (
              <span key={p.id} className="text-xs pl-2 pr-1 py-1 bg-[#1a1a1a] border border-[#27272a] rounded-md text-zinc-300 inline-flex items-center gap-1">
                {p.name}
                <button onClick={() => remove(p.id)} disabled={busy} className="ml-1 text-zinc-500 hover:text-[#ff0026]" data-testid={`remove-player-${p.id}`}>
                  <X size={12} weight="bold" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <select className="input" value={picker} onChange={(e) => setPicker(e.target.value)} data-testid="add-player-picker">
              <option value="">Add player…</option>
              {remaining.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button onClick={add} disabled={!picker || busy} className="btn-secondary" data-testid="add-player-btn">
              <Plus size={14} weight="bold" /> Add
            </button>
          </div>
        </div>

        <div className="border-t border-[#27272a] pt-4">
          <div className="font-label mb-2">Dispatch to a coach</div>
          <p className="text-xs text-zinc-500 mb-2">
            Assigns every player in this squad to the chosen coach in one click.
          </p>
          <div className="flex gap-2">
            <select className="input" value={coachPick} onChange={(e) => setCoachPick(e.target.value)} data-testid="dispatch-coach-select">
              <option value="">Pick coach…</option>
              {coaches.map((c) => <option key={c.id} value={c.id}>{`${c.name} (${c.role})`}</option>)}
            </select>
            <button onClick={dispatchSquad} disabled={!coachPick || busy} className="btn-primary" data-testid="dispatch-btn">
              <PaperPlaneTilt size={14} weight="bold" /> Dispatch
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
